import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import FeedItem from "../components/FeedItem";
import FirstWinNudgeCard from "../components/FirstWinNudgeCard";
import ProofUnlockedCard from "../components/ProofUnlockedCard";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import {
    Spinner, Fire, Clock, Lightning, Compass, Heart, Flask, ArrowRight, CheckCircle,
    Question, BookOpen, Newspaper, Bug, Flag, Trophy, Bookmark, MagnifyingGlass, HardDrives,
    ArrowsClockwise,
} from "@phosphor-icons/react";
import SEOHead from "../components/SEOHead";
import OnboardingChecklist from "../components/OnboardingChecklist";
import { ContinueCard } from "../components/ContinueCard";
import { AnswerPin } from "../components/AnswerPin";
import SignupWelcomeStrip from "../components/SignupWelcomeStrip";
import LivePulseStrip from "../components/LivePulseStrip";
import HackerStatsBar from "../components/HackerStatsBar";

const LIMIT = 5;

// Content-type filter — each option knows its own data source and how to render
// the item. "all" stays on the existing mixed `/api/feed` endpoint.
const CATEGORIES = [
    { id: "all", label: "All", icon: Compass },
    { id: "questions", label: "Questions", icon: Question },
    { id: "writeups", label: "Writeups", icon: BookOpen },
    { id: "news", label: "News", icon: Newspaper },
    { id: "ctf", label: "CTFs", icon: Flag },
    { id: "bounties", label: "Bounties", icon: Bug },
    { id: "tournaments", label: "Tournaments", icon: Trophy },
    { id: "flex", label: "Flex Wall", icon: Fire },
];

export default function HomePage() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [params] = useSearchParams();
    const tag = params.get("tag");
    const [sort, setSort] = useState("new");
    const [feedKind, setFeedKind] = useState("explore");      // explore | following
    const [zeroWin, setZeroWin] = useState(false);              // DEV-OCT-006: no meaningful action yet
    useEffect(() => {
        if (!user) { setZeroWin(false); return; }
        api.get("/me/next-best-action").then(({ data }) => setZeroWin(data?.type === "first_flag")).catch(() => setZeroWin(false));
    }, [user]);
    const [followingSort, setFollowingSort] = useState("new");
    const [category, setCategory] = useState("all");           // see CATEGORIES
    const sentinelRef = useRef(null);
    // Pull-to-refresh (touch devices) — native-app feel on the phone feed.
    const touchStartY = useRef(null);
    const [pull, setPull] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const load = async (offset = 0, append = false) => {
        if (append) setLoadingMore(true); else setLoading(true);
        try {
            let data;
            // Tag filter wins over category — keeps `?tag=xxx` deep links working.
            if (tag) {
                const r = await api.get(`/questions?tag=${tag}&sort=${sort}&limit=${LIMIT}&offset=${offset}`);
                data = r.data.map((q) => ({ ...q, kind: "question" }));
            } else if (feedKind === "following" && user) {
                const r = await api.get(`/me/feed?sort=${followingSort}&limit=${LIMIT}&offset=${offset}`);
                // Backend now stamps kind directly (question | post | ctf | article | bounty).
                // Fall back to type→kind mapping for any legacy item shape.
                data = r.data.map((it) => ({
                    ...it,
                    kind: it.kind || (it.type === "question" ? "question" : "post"),
                }));
            } else if (category === "questions") {
                const r = await api.get(`/questions?sort=${sort}&limit=${LIMIT}&offset=${offset}`);
                data = r.data.map((q) => ({ ...q, kind: "question" }));
            } else if (category === "writeups") {
                // Writeups live in the community feed as posts tagged with #writeup.
                // The articles endpoint covers manual writeups, the questions endpoint
                // covers ones tagged #writeup — fetch articles first since they're
                // the dominant source.
                const r = await api.get(`/articles?category=writeup&limit=${LIMIT}&offset=${offset}`);
                data = (r.data?.items || r.data || []).map((a) => ({ ...a, kind: "article" }));
            } else if (category === "news") {
                const r = await api.get(`/articles?category=news&limit=${LIMIT}&offset=${offset}`);
                data = (r.data?.items || r.data || []).map((a) => ({ ...a, kind: "article" }));
            } else if (category === "ctf") {
                const r = await api.get(`/ctf/challenges?active=true&limit=${LIMIT}&offset=${offset}`);
                data = (r.data?.items || r.data || []).map((c) => ({ ...c, kind: "ctf" }));
            } else if (category === "bounties") {
                const r = await api.get(`/bounties?sort=new&limit=${LIMIT}&offset=${offset}`);
                data = (r.data?.items || r.data || []).map((b) => ({ ...b, kind: "bounty" }));
            } else if (category === "tournaments") {
                const r = await api.get(`/tournaments?limit=${LIMIT + offset}`);
                const arr = (r.data?.items || r.data || []).slice(offset, offset + LIMIT);
                data = arr.map((t) => ({ ...t, kind: "tournament" }));
            } else if (category === "flex") {
                const r = await api.get(`/feed/flex?limit=${LIMIT}&offset=${offset}`);
                data = r.data.map((p) => ({ ...p, kind: "post" }));
            } else {
                const r = await api.get(`/feed?limit=${LIMIT}&offset=${offset}`);
                data = r.data;
            }
            setHasMore(data.length === LIMIT);
            setItems(append ? (prev) => [...prev, ...data] : data);
        } catch { /* noop */ }
        setLoading(false);
        setLoadingMore(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(0, false); }, [tag, sort, feedKind, followingSort, category]);

    // Load-more (button-driven, no infinite scroll per product decision).

    // Sort options only apply to mixed feed + questions — other categories use
    // their endpoint's default order.
    const supportsSortBar = !tag && feedKind === "explore" && (category === "all" || category === "questions");

    const onTouchStart = (e) => {
        touchStartY.current = window.scrollY <= 0 ? e.touches[0].clientY : null;
    };
    const onTouchMove = (e) => {
        if (touchStartY.current === null || refreshing) return;
        const dy = e.touches[0].clientY - touchStartY.current;
        if (dy > 0 && window.scrollY <= 0) setPull(Math.min(Math.round(dy * 0.4), 72));
        else setPull(0);
    };
    const onTouchEnd = async () => {
        const shouldRefresh = pull >= 56 && !refreshing;
        touchStartY.current = null;
        if (shouldRefresh) {
            setRefreshing(true);
            setPull(0);
            await load(0, false);
            setRefreshing(false);
        } else {
            setPull(0);
        }
    };

    return (
        <div className="space-y-4" data-testid="home-feed" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
            {(pull > 0 || refreshing) && (
                <div className="sm:hidden flex items-end justify-center overflow-hidden" style={{ height: refreshing ? 40 : pull }} data-testid="pull-refresh-indicator">
                    <span className="flex items-center gap-2 pb-2 text-[10px] uppercase tracking-widest font-mono text-cyan-400">
                        <ArrowsClockwise size={14} className={refreshing ? "animate-spin" : pull >= 56 ? "rotate-180 transition-transform" : "transition-transform"} />
                        {refreshing ? "refreshing…" : pull >= 56 ? "release to refresh" : "pull to refresh"}
                    </span>
                </div>
            )}
            <SEOHead
                title={tag ? `Posts tagged #${tag}` : "Home Feed"}
                description={tag ? `Browse the latest questions and writeups tagged #${tag} on hacking.community.` : "The live feed of hacker Q&A, CTF writeups, and breaking infosec news from the hacking.community."}
                path={tag ? `/t/${encodeURIComponent(tag)}` : "/"}
            />
            {!tag && <SignupWelcomeStrip />}
            {/* DEV-OCT-006: zero-win accounts see ONE primary First Flag card first, Profile setup second,
                and every advanced module collapsed (still reachable by keyboard) until the first win. */}
            {zeroWin && user && !tag && (
                <div data-testid="zero-win-primary" className="space-y-1">
                    <AnswerPin />
                    <ContinueCard />
                    <p className="text-[11px] font-mono text-slate-500 px-1">// the first challenge takes about two minutes · no email verification needed</p>
                </div>
            )}
            {!tag && <OnboardingChecklist />}
            {zeroWin && user && !tag ? (
                <details className="group border border-white/10 rounded-sm" data-testid="zero-win-advanced">
                    <summary className="cursor-pointer list-none min-h-[44px] px-4 flex items-center justify-between text-[10px] uppercase tracking-widest font-mono text-slate-400 hover:text-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-400 rounded-sm">
                        <span>// more for experienced hackers — stats, rankings, competitions, lab of the day</span>
                        <span aria-hidden="true" className="transition-transform group-open:rotate-180">⌄</span>
                    </summary>
                    <div className="p-3 space-y-3">
                        <HackerStatsBar />
                        <LivePulseStrip />
                        <XpBoostBanner />
                        <TrackRankCard />
                        <LabOfTheDayCard />
                        <UpcomingCompsCard />
                    </div>
                </details>
            ) : (<>
            {user && !tag && <FirstWinNudgeCard />}
            {user && !tag && <ProofUnlockedCard />}

            {user && !tag && <HackerStatsBar />}

            {/* Live pulse strip — today's counts + online-now, polls /api/pulse every 25s */}
            {!tag && feedKind === "explore" && <LivePulseStrip />}
            </>)}

            <div className="pb-4">
                <div className="flex items-center gap-2.5 mb-1.5">
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                        {tag ? <>#<span className="text-cyan-400">{tag}</span></> : (feedKind === "following" && user ? t("home.your_feed") : t("home.live_feed"))}
                    </h1>
                    <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse-dot" />
                </div>
                <p className="text-sm text-slate-400">
                    {feedKind === "following" && user
                        ? t("home.your_feed_desc")
                        : t("home.live_feed_desc")}
                </p>
            </div>

            {!zeroWin && user && !tag && <XpBoostBanner />}
            {!zeroWin && user && !tag && <TrackRankCard />}
            {user && !tag && <ResumeStrip />}
            {user && !tag && <NextUpCard />}
            {!zeroWin && user && !tag && <LabOfTheDayCard />}
            {!zeroWin && user && !tag && <UpcomingCompsCard />}

            {user && !tag && (
                <div className="hidden sm:flex items-center gap-1 border-b border-white/10" data-testid="feed-kind-tabs">
                    {[
                        ["explore", t("home.explore"), Compass],
                        ["following", t("home.following"), Heart],
                    ].map(([key, label, Icon]) => (
                        <button
                            key={key}
                            onClick={() => setFeedKind(key)}
                            className={`flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-widest transition-colors ${feedKind === key ? "text-cyan-400 border-b-2 border-cyan-400" : "text-slate-500 hover:text-slate-300"}`}
                            data-testid={`feed-tab-${key}`}
                        >
                            <Icon size={12} weight="duotone" /> {label}
                        </button>
                    ))}
                </div>
            )}

            {/* Content-type chips + sort icons — single row, sort pushed right.
                Shown for both Explore and Following. When a non-All category is
                picked while on Following, snap back to Explore since /me/feed
                doesn't carry CTFs / News / Bounties. */}
            {!tag && (
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 hstrip" data-testid="feed-category-tabs">
                    {user && (
                        <>
                            {[
                                ["explore", t("home.explore"), Compass],
                                ["following", t("home.following"), Heart],
                            ].map(([key, label, Icon]) => (
                                <button
                                    key={key}
                                    onClick={() => setFeedKind(key)}
                                    data-testid={`feed-pill-${key}`}
                                    className={`sm:hidden shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide rounded-full ring-1 transition-colors ${
                                        feedKind === key
                                            ? "ring-cyan-400/60 text-slate-950 bg-cyan-400"
                                            : "ring-white/10 text-slate-400 bg-white/[0.03]"
                                    }`}
                                >
                                    <Icon size={12} weight="duotone" /> {label}
                                </button>
                            ))}
                            <span className="sm:hidden shrink-0 w-px h-4 bg-white/10 mx-0.5" aria-hidden="true" />
                        </>
                    )}
                    {CATEGORIES.map((c) => {
                        const Icon = c.icon;
                        const active = category === c.id && (c.id === "all" || feedKind === "explore");
                        return (
                            <button
                                key={c.id}
                                onClick={() => {
                                    setCategory(c.id);
                                    if (c.id !== "all" && feedKind === "following") setFeedKind("explore");
                                }}
                                data-testid={`category-${c.id}`}
                                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium tracking-wide rounded-full ring-1 transition-colors ${
                                    active
                                        ? "ring-cyan-500/40 text-cyan-300 bg-cyan-500/15"
                                        : "ring-white/5 text-slate-400 bg-white/[0.03] hover:text-slate-100 hover:bg-white/[0.06]"
                                }`}
                            >
                                <Icon size={12} weight="duotone" /> {t(`home.cat_${c.id}`, c.label)}
                            </button>
                        );
                    })}
                    {/* Sort icons — pushed to the right, icon-only. Explore uses
                        new/top/active across /feed + /questions; Following uses
                        new/hot for /me/feed. */}
                    {feedKind === "explore" && supportsSortBar && (
                        <div className="ml-auto flex items-center gap-1 shrink-0 pl-2 border-l border-white/8">
                            {[["new", t("home.latest"), Clock], ["top", t("home.top"), Fire], ["active", t("home.active"), Lightning]].map(([key, label, Icon]) => (
                                <button
                                    key={key}
                                    onClick={() => setSort(key)}
                                    data-testid={`sort-${key}`}
                                    title={label}
                                    aria-label={`Sort by ${label}`}
                                    className={`shrink-0 p-1.5 border rounded-sm transition-colors ${
                                        sort === key
                                            ? "border-cyan-500/40 text-cyan-300 bg-cyan-500/10"
                                            : "border-white/10 text-slate-500 hover:text-slate-200 hover:border-slate-700"
                                    }`}
                                >
                                    <Icon size={13} weight="duotone" />
                                </button>
                            ))}
                        </div>
                    )}
                    {feedKind === "following" && user && (
                        <div className="ml-auto flex items-center gap-1 shrink-0 pl-2 border-l border-white/8" data-testid="following-sort-bar">
                            {[["new", t("home.newest"), Clock], ["hot", t("home.hot"), Fire]].map(([key, label, Icon]) => (
                                <button
                                    key={key}
                                    onClick={() => setFollowingSort(key)}
                                    data-testid={`following-sort-${key}`}
                                    title={label}
                                    aria-label={`Sort by ${label}`}
                                    className={`shrink-0 p-1.5 border rounded-sm transition-colors ${
                                        followingSort === key
                                            ? "border-cyan-500/40 text-cyan-300 bg-cyan-500/10"
                                            : "border-white/10 text-slate-500 hover:text-slate-200 hover:border-slate-700"
                                    }`}
                                >
                                    <Icon size={13} weight="duotone" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!zeroWin && user && !tag && feedKind === "explore" && <><AnswerPin /><ContinueCard /></>}
            {!tag && category === "flex" && feedKind === "explore" && <FireLeadersStrip />}

            {loading ? (
                <div className="flex items-center justify-center py-16 text-slate-500">
                    <Spinner size={24} className="animate-spin" />
                </div>
            ) : items.length === 0 ? (
                <EmptyState feedKind={feedKind} category={category} setFeedKind={setFeedKind} setCategory={setCategory} t={t} />
            ) : (
                <div className="space-y-2.5" data-testid="feed-list">
                    {items.map((item, idx) => (
                        <FeedItemRouter key={item.question_id || item.post_id || item.article_id || item.challenge_id || item.bounty_id || item.tournament_id || idx} item={item} index={idx} onChange={() => load(0, false)} />
                    ))}
                </div>
            )}

            {hasMore && items.length > 0 && (
                <div className="py-6 flex items-center justify-center" data-testid="feed-load-more-wrap">
                    <button onClick={() => load(items.length, true)} disabled={loadingMore} ref={sentinelRef}
                            data-testid="feed-load-more"
                            className="w-full sm:w-auto justify-center text-[11px] uppercase tracking-widest font-mono border border-slate-700 hover:border-cyan-500/50 text-slate-300 hover:text-cyan-300 px-6 py-2.5 rounded-sm transition-colors flex items-center gap-2">
                        {loadingMore ? (<><Spinner size={14} className="animate-spin text-cyan-400" /> loading…</>) : "load more"}
                    </button>
                </div>
            )}
            {!hasMore && items.length > 0 && (
                <div className="py-6 text-center text-[10px] uppercase tracking-widest text-slate-600 font-mono" data-testid="feed-end">
                    // you've reached the bottom
                </div>
            )}
        </div>
    );
}


/** Dispatches to a card style based on the item kind. Question/post fall back
 *  to the existing FeedItem; new categories render compact cards inline.
 */
function FeedItemRouter({ item, index, onChange }) {
    if (item.kind === "article") return <ArticleCard item={item} index={index} />;
    if (item.kind === "ctf") return <CtfCard item={item} index={index} />;
    if (item.kind === "bounty") return <BountyCard item={item} index={index} />;
    if (item.kind === "tournament") return <TournamentCard item={item} index={index} />;
    return <FeedItem item={item} index={index} onChange={onChange} />;
}


function EmptyState({ feedKind, category, setFeedKind, setCategory, t }) {
    return (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-sm bg-grid" data-testid="feed-empty">
            {feedKind === "following" ? (
                <>
                    <div className="text-slate-500 text-sm font-mono">{t("home.empty_following")}</div>
                    <button onClick={() => setFeedKind("explore")} className="text-cyan-400 hover:text-cyan-300 text-xs uppercase tracking-widest mt-2 inline-block">
                        {t("home.explore_communities")}
                    </button>
                </>
            ) : category !== "all" ? (
                <>
                    <div className="text-slate-500 text-sm font-mono">// nothing in this category yet</div>
                    <button onClick={() => setCategory("all")} className="text-cyan-400 hover:text-cyan-300 text-xs uppercase tracking-widest mt-2 inline-block">
                        show all
                    </button>
                </>
            ) : (
                <>
                    <div className="text-slate-500 text-sm font-mono">{t("home.empty_feed")}</div>
                    <Link to="/ask" className="text-cyan-400 hover:text-cyan-300 text-xs uppercase tracking-widest mt-2 inline-block" data-testid="empty-ask-link">
                        {t("home.be_first_to_ask")}
                    </Link>
                </>
            )}
        </div>
    );
}


function ArticleCard({ item, index }) {
    const delay = (index % 5) + 1;
    const cat = item.category === "writeup" ? "writeup" : "news";
    const Icon = cat === "writeup" ? BookOpen : Newspaper;
    return (
        <Link
            to={`/news/${item.slug || item.article_id}`}
            data-testid={`feed-article-${item.article_id}`}
            className={`block group hc-card hc-card-interactive p-4 animate-fadein-${delay}`}
        >
            <div className="flex flex-nowrap items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5 min-w-0 overflow-hidden">
                <Icon size={11} weight="duotone" className="text-cyan-400 shrink-0" />
                <span className="text-cyan-300 shrink-0">// {cat}</span>
                <span className="shrink-0">·</span>
                <span className="shrink-0 whitespace-nowrap">{timeAgo(item.published_at || item.created_at)}</span>
                {item.author_username && <><span className="shrink-0">·</span><span className="truncate min-w-0">by @{item.author_username}</span></>}
            </div>
            <div className="text-sm sm:text-base font-bold text-slate-100 group-hover:text-cyan-300 leading-snug break-words line-clamp-2" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                {item.title}
            </div>
            {item.summary && (
                <div className="text-xs text-slate-400 mt-1 line-clamp-2 break-words">{item.summary}</div>
            )}
        </Link>
    );
}


function CtfCard({ item, index }) {
    const delay = (index % 5) + 1;
    const diffColor = { easy: "text-emerald-300", medium: "text-amber-300", hard: "text-rose-300" }[item.difficulty] || "text-slate-300";
    return (
        <Link
            to={`/ctf/${item.challenge_id}`}
            data-testid={`feed-ctf-${item.challenge_id}`}
            className={`block group hc-card hc-card-interactive p-4 animate-fadein-${delay}`}
        >
            <div className="flex flex-nowrap items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5 min-w-0 overflow-hidden">
                <Flag size={11} weight="duotone" className="text-cyan-400 shrink-0" />
                <span className="text-cyan-300 shrink-0">// ctf</span>
                <span className="shrink-0">·</span>
                <span className="truncate min-w-0">{item.category}</span>
                <span className="shrink-0">·</span>
                <span className={`shrink-0 ${diffColor}`}>{item.difficulty}</span>
                <span className="shrink-0">·</span>
                <span className="shrink-0 whitespace-nowrap">+{item.points || 100} pts</span>
            </div>
            <div className="text-sm sm:text-base font-bold text-slate-100 group-hover:text-cyan-300 leading-snug break-words line-clamp-2" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                {item.title}
            </div>
            {item.description && (
                <div className="text-xs text-slate-400 mt-1 line-clamp-2 break-words">{item.description}</div>
            )}
        </Link>
    );
}


function BountyCard({ item, index }) {
    const delay = (index % 5) + 1;
    const min = item.min_reward || 0;
    const max = item.max_reward || 0;
    return (
        <a
            href={item.url || ((item.slug || item.bounty_id) ? `/bounties/${item.slug || item.bounty_id}` : "/bounties")}
            target={item.url ? "_blank" : "_self"}
            rel={item.url ? "noopener noreferrer" : undefined}
            data-testid={`feed-bounty-${item.bounty_id}`}
            className={`block group hc-card hc-card-interactive p-4 animate-fadein-${delay}`}
        >
            <div className="flex flex-nowrap items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5 min-w-0 overflow-hidden">
                <Bug size={11} weight="duotone" className="text-cyan-400 shrink-0" />
                <span className="text-cyan-300 shrink-0">// bounty</span>
                <span className="shrink-0">·</span>
                <span className="truncate min-w-0">{item.platform}</span>
                {(min || max) ? <><span className="shrink-0">·</span><span className="shrink-0 whitespace-nowrap text-emerald-300">${min.toLocaleString()}-${max.toLocaleString()}</span></> : null}
            </div>
            <div className="text-sm sm:text-base font-bold text-slate-100 group-hover:text-cyan-300 leading-snug break-words line-clamp-2" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                {item.name}
            </div>
            {item.description && (
                <div className="text-xs text-slate-400 mt-1 line-clamp-2 break-words">{item.description}</div>
            )}
        </a>
    );
}


function TournamentCard({ item, index }) {
    const delay = (index % 5) + 1;
    const startsAt = item.scheduled_start ? new Date(item.scheduled_start) : null;
    const isLive = item.status === "active";
    const isUpcoming = item.status === "scheduled";
    const statusColor = isLive ? "text-emerald-300" : isUpcoming ? "text-amber-300" : "text-slate-500";
    const startsLabel = startsAt ? (
        isLive
            ? "live now"
            : isUpcoming
                ? `starts ${startsAt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`
                : `ended ${timeAgo(item.scheduled_start)}`
    ) : "";
    return (
        <Link
            to={`/tournaments`}
            data-testid={`feed-tournament-${item.tournament_id}`}
            className={`block group hc-card hc-card-interactive p-4 animate-fadein-${delay}`}
        >
            <div className="flex flex-nowrap items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5 min-w-0 overflow-hidden">
                <Trophy size={11} weight="duotone" className="text-cyan-400 shrink-0" />
                <span className="text-cyan-300 shrink-0">// tournament</span>
                <span className="shrink-0">·</span>
                <span className={`shrink-0 ${statusColor}`}>{item.status}</span>
                {item.lab_title && <><span className="shrink-0">·</span><span className="truncate min-w-0">{item.lab_title}</span></>}
                {item.difficulty && <><span className="shrink-0">·</span><span className="shrink-0">{item.difficulty}</span></>}
            </div>
            <div className="text-sm sm:text-base font-bold text-slate-100 group-hover:text-cyan-300 leading-snug break-words line-clamp-2" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                {item.title}
            </div>
            <div className="text-xs text-slate-400 mt-1 flex items-center gap-3 flex-wrap">
                {startsLabel && <span>{startsLabel}</span>}
                <span>· {item.participants_count || 0}/{item.max_players || 16} players</span>
                <span>· {item.duration_minutes || 60} min</span>
            </div>
            {item.description && (
                <div className="text-xs text-slate-400 mt-1 line-clamp-2 break-words">{item.description}</div>
            )}
        </Link>
    );
}


function timeAgo(iso) {
    if (!iso) return "";
    const secs = (Date.now() - new Date(iso).getTime()) / 1000;
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
    if (secs < 604800) return `${Math.floor(secs / 86400)}d`;
    return new Date(iso).toLocaleDateString();
}


function XpBoostBanner() {
    const [boost, setBoost] = useState(null);
    useEffect(() => {
        api.get("/xp/boost").then(({ data }) => setBoost(data)).catch(() => {});
    }, []);
    if (!boost?.active) return null;
    return (
        <div data-testid="xp-boost-banner" className="border border-amber-500/40 bg-amber-500/10 rounded-sm px-3.5 py-2.5 flex flex-nowrap items-center gap-2 min-w-0">
            <Lightning size={14} weight="fill" className="shrink-0 text-amber-300" />
            <span className="text-[11px] uppercase tracking-widest font-mono text-amber-200 truncate min-w-0">// double xp weekend — everything counts 2x until monday 00:00 utc</span>
        </div>
    );
}


function UpcomingCompsCard() {
    const [comps, setComps] = useState(null);
    useEffect(() => {
        api.get("/directory/events/external?limit=6").then(({ data }) => {
            const now = Date.now();
            const merged = [...(data.items || []), ...(data.hackathons || [])]
                .filter((e) => e.start && new Date(e.start).getTime() > now)
                .sort((a, b) => new Date(a.start) - new Date(b.start))
                .slice(0, 3);
            setComps(merged);
        }).catch(() => setComps([]));
    }, []);
    if (!comps || comps.length === 0) return null;
    return (
        <div className="border border-amber-500/30 bg-gradient-to-r from-amber-500/[0.07] via-amber-500/[0.03] to-transparent rounded-sm p-3.5" data-testid="upcoming-comps-card">
            <div className="flex flex-nowrap items-center gap-2 min-w-0 mb-1">
                <Trophy size={14} weight="duotone" className="shrink-0 text-amber-300" />
                <span className="text-[10px] uppercase tracking-widest font-mono text-amber-300 whitespace-nowrap overflow-hidden text-ellipsis min-w-0">// next competitions worldwide</span>
                <Link to="/events" data-testid="upcoming-comps-all" className="ml-auto shrink-0 text-[10px] uppercase tracking-widest font-mono text-cyan-400 hover:text-cyan-300 flex items-center gap-1 whitespace-nowrap">all events <ArrowRight size={10} /></Link>
            </div>
            {comps.map((e) => (
                <a key={e.event_id} href={e.url || e.ctftime_url} target="_blank" rel="noopener nofollow noreferrer"
                   data-testid={`upcoming-comp-${e.event_id}`}
                   className="group flex flex-nowrap items-center gap-2 min-w-0 py-1.5 border-t border-white/5 first-of-type:border-t-0">
                    <span className="shrink-0 w-14 text-[10px] uppercase tracking-widest font-mono text-amber-300 whitespace-nowrap">
                        {new Date(e.start).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                    <span className="truncate min-w-0 text-xs font-mono text-slate-300 group-hover:text-cyan-300 transition-colors">{e.title}</span>
                    <span className="ml-auto shrink-0 text-[10px] uppercase tracking-widest font-mono text-slate-500 group-hover:text-cyan-400 flex items-center gap-1 whitespace-nowrap">
                        {e.source === "devpost" ? "hackathon" : "ctf"} <ArrowRight size={10} />
                    </span>
                </a>
            ))}
        </div>
    );
}


function LabOfTheDayCard() {
    const [lotd, setLotd] = useState(null);
    useEffect(() => { api.get("/labs/lab-of-the-day").then(({ data }) => setLotd(data)).catch(() => {}); }, []);
    if (!lotd) return null;
    const diffColor = {
        easy: "text-emerald-300 border-emerald-500/40",
        medium: "text-amber-300 border-amber-500/40",
        hard: "text-rose-300 border-rose-500/40",
    }[lotd.difficulty] || "text-slate-300 border-slate-700";
    return (
        <Link to={`/labs/${lotd.lab_id}`} data-testid="lab-of-the-day"
              className="block border border-white/10 bg-gradient-to-r from-cyan-500/[0.07] via-cyan-500/[0.03] to-transparent rounded-sm p-4 hover:border-cyan-500/40 transition-colors">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-cyan-300 mb-1 min-w-0">
                        <Flask size={14} weight="duotone" className="shrink-0" />
                        <span className="text-[10px] uppercase tracking-widest font-mono whitespace-nowrap overflow-hidden text-ellipsis min-w-0">// lab of the day · {lotd.date}</span>
                        {lotd.already_solved && <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono flex items-center gap-1 shrink-0"><CheckCircle size={10} weight="fill" /> solved</span>}
                    </div>
                    <div className="text-base sm:text-lg font-bold text-slate-100" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>{lotd.title}</div>
                    <div className="text-xs text-slate-400 mt-1 line-clamp-2 break-words">{lotd.brief}</div>
                    <div className="sm:hidden mt-2 flex items-center gap-2 min-w-0">
                        <span className={`shrink-0 text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm ${diffColor}`}>{lotd.difficulty}</span>
                        {!lotd.already_solved && (
                            <span className="text-[10px] uppercase tracking-widest text-cyan-300 font-mono whitespace-nowrap overflow-hidden text-ellipsis min-w-0">+{lotd.base_xp} XP <span className="text-amber-300">+{lotd.bonus_xp} bonus</span></span>
                        )}
                        <span className="ml-auto shrink-0 text-[10px] uppercase tracking-widest text-cyan-400 font-mono flex items-center gap-1">attack <ArrowRight size={10} /></span>
                    </div>
                </div>
                <div className="hidden sm:flex flex-col items-end gap-1 shrink-0">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm ${diffColor}`}>{lotd.difficulty}</span>
                    {!lotd.already_solved && (
                        <span className="text-[10px] uppercase tracking-widest text-cyan-300 font-mono">+{lotd.base_xp} XP <span className="text-amber-300">+{lotd.bonus_xp} bonus</span></span>
                    )}
                    <span className="text-[10px] uppercase tracking-widest text-cyan-400 font-mono flex items-center gap-1">attack <ArrowRight size={10} /></span>
                </div>
            </div>
        </Link>
    );
}



function NextUpCard() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/me/academy/next-up")
            .then(({ data }) => setData(data))
            .catch(() => setData(null));
    }, []);
    if (!data || data.via === "none") return null;
    // For track_complete state, surface a celebratory card linking back to /tracks
    if (data.via === "track_complete") {
        return (
            <Link to={data.url || "/tracks"} data-testid="next-up-card"
                  className="block border border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 to-transparent rounded-sm p-4 hover:border-emerald-400 transition-colors">
                <div className="flex items-center gap-3">
                    <Trophy size={20} weight="fill" className="text-amber-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-mono text-emerald-300">// career track complete</div>
                        <div className="text-sm font-bold text-slate-100" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>{data.reason}</div>
                    </div>
                    <ArrowRight size={14} className="text-emerald-400 shrink-0" />
                </div>
            </Link>
        );
    }
    const accent = data.via === "track" ? (data.track_color || "violet") : "cyan";
    const KindIcon = data.kind === "lab" ? Flask : (data.kind === "ctf" ? Flag : Question);
    return (
        <Link to={data.url} data-testid="next-up-card"
              className={`block border border-white/10 bg-gradient-to-r from-${accent}-500/10 via-${accent}-500/5 to-transparent rounded-sm p-4 hover:border-${accent}-400 transition-colors`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className={`flex items-center gap-2 text-${accent}-300 mb-1`}>
                        {data.via === "track" ? <Bookmark size={12} weight="fill" /> : <Compass size={12} weight="duotone" />}
                        <span className="text-[10px] uppercase tracking-widest font-mono">// {data.reason}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                        <KindIcon size={13} weight="duotone" className={`text-${accent}-400 shrink-0`} />
                        <span className="text-base sm:text-lg font-bold text-slate-100" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>{data.title}</span>
                    </div>
                    <div className="text-xs text-slate-400 line-clamp-2">{data.brief || `${data.domain_title} · ${data.level_label}`}</div>
                    <div className="sm:hidden mt-2 flex items-center gap-2 min-w-0">
                        <span className={`shrink-0 text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm text-${accent}-300 border-${accent}-500/40 bg-${accent}-500/5 font-mono`}>{data.kind}</span>
                        <span className={`ml-auto shrink-0 text-[10px] uppercase tracking-widest text-${accent}-400 font-mono flex items-center gap-1`}>start <ArrowRight size={10} /></span>
                    </div>
                </div>
                <div className="hidden sm:flex flex-col items-end gap-1 shrink-0">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm text-${accent}-300 border-${accent}-500/40 bg-${accent}-500/5 font-mono`}>{data.kind}</span>
                    <span className={`text-[10px] uppercase tracking-widest text-${accent}-400 font-mono flex items-center gap-1`}>start <ArrowRight size={10} /></span>
                </div>
            </div>
        </Link>
    );
}


function TrackRankCard() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/me/career-track/rank")
            .then(({ data }) => setData(data))
            .catch(() => setData(null));
    }, []);
    if (!data || !data.pinned) return null;

    // Rank-suffix helper — turns 1/2/3/4 into "#1"/"#2"/"#3"/"#4" with the
    // appropriate medal for the top three (the visible top of any healthy
    // cohort) and a plain rank chip for everyone else.
    const medal = data.rank === 1 ? "🥇" : data.rank === 2 ? "🥈" : data.rank === 3 ? "🥉" : null;
    const cohortBlurb = data.cohort_size === 1
        ? "You're the first one on this track — invite peers"
        : data.above
            ? `${data.above.stages_ahead === 0 ? "Tied with" : `${data.above.stages_ahead} stage${data.above.stages_ahead > 1 ? "s" : ""} behind`} @${data.above.username}`
            : data.below
                ? `${data.below.stages_behind === 0 ? "Tied with" : `${data.below.stages_behind} stage${data.below.stages_behind > 1 ? "s" : ""} ahead of`} @${data.below.username}`
                : "Top of the leaderboard";

    return (
        <Link to={`/tracks/${data.slug}`} data-testid="track-rank-card"
              className={`block border border-white/10 bg-gradient-to-r from-${data.color}-500/10 via-${data.color}-500/5 to-transparent hover:border-${data.color}-400 rounded-sm p-3 transition-colors`}>
            <div className="flex items-center gap-3">
                <div className={`shrink-0 w-12 h-12 rounded-sm border border-${data.color}-500/40 bg-${data.color}-500/10 flex items-center justify-center`}>
                    <Trophy size={22} weight="duotone" className={`text-${data.color}-400`} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className={`text-[10px] uppercase tracking-widest font-mono text-${data.color}-300`}>// on the {data.title.toLowerCase()} track</div>
                    <div className="text-sm font-bold text-slate-100 mt-0.5 flex items-center gap-1.5 flex-wrap" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                        <span data-testid="track-rank-position">{medal ? `${medal} ` : ""}#{data.rank} of {data.cohort_size}</span>
                        <span className="text-[10px] uppercase tracking-widest font-mono text-slate-500">·</span>
                        <span className="text-[11px] font-mono text-slate-400">{data.stages_completed}/{data.stage_count} stages</span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{cohortBlurb}</div>
                </div>
                <ArrowRight size={14} className={`shrink-0 text-${data.color}-400`} />
            </div>
        </Link>
    );
}


// "Resume where you left off" — started-but-unfinished investigations + network-lab
// campaigns, highest progress first. Complements NextUpCard (Academy) & LabOfTheDay.
function ResumeStrip() {
    const [items, setItems] = useState(null);
    const [streak, setStreak] = useState(0);
    useEffect(() => {
        api.get("/me/resume").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
        api.get("/me/academy/streak").then(({ data }) => setStreak(data.streak || 0)).catch(() => {});
    }, []);
    if (!items || items.length === 0) return null;
    const ICON = { investigation: MagnifyingGlass, "network-lab": HardDrives, track: Compass, certification: Trophy, ctf: Flag, lab: Flask, academy: BookOpen };
    const KIND_LABEL = { investigation: "investigation", "network-lab": "network lab", track: "career track", certification: "certification", ctf: "ctf", lab: "lab", academy: "academy" };
    return (
        <div data-testid="resume-strip" className="space-y-2">
            <div className="flex items-center gap-2 text-slate-400">
                <Clock size={13} weight="duotone" className="text-amber-300" />
                <span className="text-[10px] uppercase tracking-widest font-mono">// resume where you left off</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
                {items.map((it, idx) => {
                    const Icon = ICON[it.kind] || Flask;
                    return (
                        <Link key={it.url} to={it.url} data-testid={`resume-item-${it.kind}`}
                              className={`group block border border-white/10 bg-gradient-to-r from-${it.accent}-500/10 to-transparent rounded-sm p-2.5 sm:p-3 hover:border-${it.accent}-400 transition-colors`}>
                            <div className="flex items-center gap-2.5">
                                <div className={`shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-sm border border-${it.accent}-500/40 bg-${it.accent}-500/10 flex items-center justify-center`}>
                                    <Icon size={16} weight="duotone" className={`text-${it.accent}-300`} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className={`text-[9px] uppercase tracking-widest font-mono text-${it.accent}-300`}>{KIND_LABEL[it.kind] || it.kind}</div>
                                    <div className="text-sm font-bold text-slate-100 truncate" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>{it.title}</div>
                                </div>
                                <ArrowRight size={14} className={`hidden sm:block shrink-0 text-slate-600 group-hover:text-${it.accent}-300 group-hover:translate-x-0.5 transition-all`} />
                            </div>
                            {it.pct !== null && it.pct !== undefined ? (
                                <div className="mt-1.5 sm:mt-2 flex items-center gap-2">
                                    <div className="flex-1 min-w-[40px] h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                        <div className={`h-full bg-${it.accent}-400 transition-all`} style={{ width: `${it.pct}%` }} />
                                    </div>
                                    <span className="text-[9px] sm:text-[10px] font-mono text-slate-400 shrink-0 whitespace-nowrap">{it.label} · {it.pct}%</span>
                                    <span className={`sm:hidden shrink-0 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono text-${it.accent}-300`}>resume <ArrowRight size={10} /></span>
                                </div>
                            ) : (
                                <div className="mt-1.5 sm:mt-2 flex items-center gap-1.5">
                                    <span className={`text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded-sm border border-${it.accent}-500/30 text-${it.accent}-300`}>resume</span>
                                    <span className="text-[10px] font-mono text-slate-500 truncate min-w-0">{it.label}</span>
                                    <ArrowRight size={12} className={`sm:hidden ml-auto shrink-0 text-${it.accent}-300`} />
                                </div>
                            )}
                            {idx === 0 && streak >= 1 && (
                                <div className="mt-1.5 sm:mt-2 flex items-center gap-1.5 text-[10px] font-mono text-amber-300" data-testid="resume-streak-nudge">
                                    <Fire size={12} weight="fill" /> resume today to keep your {streak}-day streak alive
                                </div>
                            )}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

function FireLeadersStrip() {
    const [leaders, setLeaders] = useState(null);
    useEffect(() => { api.get("/flex/fire-leaders").then(({ data }) => setLeaders(data.leaders || [])).catch(() => setLeaders([])); }, []);
    if (!leaders || leaders.length === 0) return null;
    return (
        <div className="border border-orange-500/30 bg-orange-500/[0.05] rounded-sm px-3.5 py-2.5" data-testid="fire-leaders-strip">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-300/80 mb-2">// hall of fame — most-fired flexes this month</div>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                {leaders.map((l, i) => (
                    <Link key={l.post_id} to={`/p/${l.post_id}`} data-testid={`fire-leader-${i}`}
                          className="inline-flex items-center gap-1.5 font-mono text-[11px] text-slate-300 hover:text-white transition-colors">
                        <span className="text-slate-600">#{i + 1}</span>
                        <span className="text-orange-300">🔥{l.fire_count}</span>
                        <span className="truncate max-w-[220px]">{l.title}</span>
                        <span className="text-slate-500">@{l.author_username}</span>
                    </Link>
                ))}
            </div>
        </div>
    );
}

